/**
 * カスタム日付ピッカー(■14・2026-08-28 会議)。
 * ネイティブの date input では曜日配色ができないため、独自カレンダーで
 * 土曜=青 / 日曜=赤 / 祝日=赤(名称ツールチップ)を表示する。
 * 祝日データは jpHolidays(■13)と共用。表示は「YYYY/MM/DD(曜)」
 */
import { useMemo, useState } from 'react';
import { jpHolidayName } from '@core/lib/jpHolidays';
import { todayStr, weekdayLabel } from '@core/lib/date';

const WD_HEAD = ['日', '月', '火', '水', '木', '金', '土'];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export default function DateField({
  value,
  onChange,
  placeholder = '日付を選択',
}: {
  /** 'YYYY-MM-DD' または ''(未選択) */
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => (value || todayStr()).slice(0, 7)); // YYYY-MM

  const today = todayStr();

  const cells = useMemo(() => {
    const [y, m] = view.split('-').map(Number) as [number, number];
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const lead = first.getDay();
    const out: Array<{ date: string; day: number } | null> = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push({ date: `${y}-${pad2(m)}-${pad2(d)}`, day: d });
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  function moveMonth(delta: number) {
    const [y, m] = view.split('-').map(Number) as [number, number];
    const d = new Date(y, m - 1 + delta, 1);
    setView(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  }

  function openPicker() {
    setView((value || todayStr()).slice(0, 7));
    setOpen(true);
  }

  const label = value ? `${value.replace(/-/g, '/')}${weekdayLabel(value)}` : '';
  const [vy, vm] = view.split('-').map(Number) as [number, number];

  return (
    <div style={{ position: 'relative' }}>
      {/* 文字サイズは他の入力欄と共通(.field-btn: PC 13.5px / スマホ 16px)。
          日付だけ小さく見える問題の対策(2026-08-31 指示) */}
      <button
        type="button"
        className="field-btn"
        onClick={openPicker}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '9px 11px',
          border: '1px solid var(--line)',
          borderRadius: 6,
          background: '#fff',
          fontFamily: 'inherit',
          color: value ? 'var(--ink)' : 'var(--muted)',
          cursor: 'pointer',
        }}
      >
        {label || placeholder}
        <span style={{ float: 'right', color: 'var(--muted)', fontSize: 12 }}>▾</span>
      </button>

      {open && (
        <>
          {/* 外側クリックで閉じる透明オーバーレイ */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 39 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 40,
              marginTop: 4,
              background: '#fff',
              border: '1px solid var(--line)',
              borderRadius: 8,
              boxShadow: '0 6px 20px rgba(0,0,0,.15)',
              padding: 10,
              width: 252,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <button type="button" className="txn-link" style={{ fontSize: 14 }} onClick={() => moveMonth(-1)}>
                ◀
              </button>
              <b style={{ flex: 1, textAlign: 'center', fontSize: 13.5 }}>
                {vy}年{vm}月
              </b>
              <button type="button" className="txn-link" style={{ fontSize: 14 }} onClick={() => moveMonth(1)}>
                ▶
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {WD_HEAD.map((w, i) => (
                <div
                  key={w}
                  style={{
                    textAlign: 'center',
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '2px 0',
                    color: i === 0 ? 'var(--red)' : i === 6 ? 'var(--blue)' : 'var(--muted)',
                  }}
                >
                  {w}
                </div>
              ))}
              {cells.map((c, i) => {
                if (!c) return <div key={`e${i}`} />;
                const holiday = jpHolidayName(c.date);
                const dow = i % 7;
                const color = holiday || dow === 0 ? 'var(--red)' : dow === 6 ? 'var(--blue)' : 'var(--ink)';
                const selected = c.date === value;
                const isToday = c.date === today;
                return (
                  <button
                    key={c.date}
                    type="button"
                    title={holiday ?? undefined}
                    onClick={() => {
                      onChange(c.date);
                      setOpen(false);
                    }}
                    style={{
                      border: isToday && !selected ? '1px solid var(--brand-blue)' : '1px solid transparent',
                      borderRadius: 6,
                      background: selected ? 'var(--brand-blue)' : holiday ? 'var(--red-bg)' : 'transparent',
                      color: selected ? '#fff' : color,
                      fontSize: 12.5,
                      padding: '4px 0',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontWeight: selected ? 700 : 400,
                    }}
                  >
                    {c.day}
                  </button>
                );
              })}
            </div>
            {/* 凡例(土=青 / 日・祝=赤)は削除(2026-08-31 指示。配色を見れば分かるため) */}
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                className="txn-link"
                style={{ fontSize: 11.5 }}
                onClick={() => {
                  onChange(today);
                  setOpen(false);
                }}
              >
                今日
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
